import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { CSSProperties, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { IPartnerContainerProps, IPartnerProfile } from "../../interfaces/PartnerView.interfaces";
import { useDropdownWithUrlSync } from "../../SharedComponents/Hooks/useDropdownWithUrlSync";
import { useGetPartnerProfilePageDataMutation } from "../../slices/ExplorePartners.slice";
import CustomDropdown from "../ProductView/Common/CustomDropdown";
import PartnerProfileChart from "./PartnerProfileChart";
import PartnerProfileTable from "./PartnerProfileTable";

export default function PartnerProfileContainer(props: IPartnerContainerProps) {
  // Properties
  const [filteredItems, setFilteredItems] = useState<IPartnerProfile[]>([]);
  const [searchParams] = useSearchParams();

  // Chart filters as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const partnerOneID = searchParams.get("partnerOneId");
  const mpnID = searchParams.get("mpnId");

  // Use the reusable hook for dropdown with URL sync
  const { selectedValue: selectedMetric, handleSelection: handleSelectedMetrics } = useDropdownWithUrlSync({
    validValues: ["All Workloads", "Security Workloads"],
    defaultValue: "Security Workloads",
    onValueChange: useCallback(() => {
      // Clear chart filters when metric changes
      setChartFilters([]);
    }, [])
  });

  const workloadType = selectedMetric === "All Workloads" ? "allup" : "securityworkload";
  const [getPartnerProfilePageData, partnerData] = useGetPartnerProfilePageDataMutation();

  useEffect(() => {
    if (partnerOneID && mpnID) {
      getPartnerProfilePageData({
        partnerOneID: partnerOneID,
        mpnIdentifier: mpnID,
        workloadIdentifier: workloadType
      });
    }
  }, [partnerOneID, mpnID, workloadType, getPartnerProfilePageData]);

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  // Events

  const showNoData = () => {
    const rootStyle: CSSProperties = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{`Partner profile details are not available for ${props.partnerOneName}.`}</div>;
  };

  const customProps = {
    partnerOneID: partnerOneID,
    partnerOneName: props?.partnerOneName
  };

  // Hooks
  useEffect(() => {
    logEvent("Partner Profile", "Partner Page", "PartnerProfileContainer", customProps);
  }, [customProps]);

  useEffect(() => {
    setFilteredItems(partnerData?.data?.partnerProfileData ?? []);

    if (partnerData.error) {
      logException(
        "Partner View",
        "PartnerProfileContainer",
        "useGetPartnerProfilePageDataQuery",
        partnerData.error,
        customProps
      );
    }
  }, [partnerData?.data, partnerData.error, partnerData]);

  // Apply chart filters
  useEffect(() => {
    const originalData = partnerData?.data?.partnerProfileData || [];

    const filteredData = originalData.filter((item: any) => {
      // If no filters, show all data
      if (chartFilters.length === 0) {
        return true;
      }

      // Apply all filters - item must match ALL active filters
      return chartFilters.every((filter) => {
        if (filter.filterKey === "partnerCountry") {
          return filter.value === item.partnerCountry;
        } else if (filter.filterKey === "isCSP") {
          return filter.value === item.isCSP;
        } else if (filter.filterKey === "isCPOR") {
          return filter.value === item.isCPOR;
        } else if (filter.filterKey === "isPAL") {
          return filter.value === item.isPAL;
        }
        return true;
      });
    });

    setFilteredItems(filteredData);
  }, [chartFilters, partnerData?.data]);

  const displayViewByMetricSelect = partnerData?.isLoading ? (
    <div style={{ flexBasis: "100%" }}>
      {" "}
      <Shimmer
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{
          root: { paddingTop: 10, paddingBottom: 10, paddingLeft: 10, width: "25%" },
          shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
        }}
        shimmerElements={[
          { type: ShimmerElementType.line, width: "15%" },
          { type: ShimmerElementType.gap, width: 10 },
          { type: ShimmerElementType.line, width: "15%" }
        ]}
      />{" "}
    </div>
  ) : (
    <FluentProvider theme={webLightTheme}>
      <CustomDropdown
        defaultOption={{
          optionText: selectedMetric,
          optionValue: selectedMetric,
          selectedOptions: [selectedMetric || ""]
        }}
        onSelectedOption={handleSelectedMetrics}
        data={[
          { value: "Security Workloads", text: "Security Workloads" },
          { value: "All Workloads", text: "All Workloads" }
        ]}
        styles={{ root: { flexBasis: "100%", paddingBottom: 10, paddingLeft: 10 } }}
      />
    </FluentProvider>
  );

  if (!partnerData.isLoading && partnerData?.error) {
    return <DisplayAPIsException error={partnerData.error} height={250} />;
  }

  return (
    <>
      {displayViewByMetricSelect}
      <PartnerProfileChart
        loading={partnerData.isLoading}
        onSelectedChartFilter={handleChartFilter}
        filteredData={filteredItems}
        associationTypeKustoLink={partnerData?.data?.associationTypeKustoLink ?? ""}
        partnerCountryKustoLink={partnerData?.data?.partnerCountryKustoLink ?? ""}
      />
      {partnerData.isLoading ? (
        <CustomShimmer columnCount={11} />
      ) : (
        (partnerData.isSuccess &&
          Array.isArray(partnerData?.data?.partnerProfileData) &&
          partnerData?.data?.partnerProfileData.length > 0 && (
            <div style={{ marginLeft: 8, marginRight: 8 }}>
              <PartnerProfileTable
                tableData={filteredItems}
                partnerOneName={props.partnerOneName}
                kustoLink={partnerData?.data?.partnerProfileKustoLink ?? ""}
              />
            </div>
          )) ||
        showNoData()
      )}
    </>
  );
}
